// Work package "access": access, onboarding and settings.
// This folder belongs to the WP (docs/v3/WORKPACKAGES.md 4). Other areas may
// import what this file exports (read-only use); they never edit this folder.
// Every route of this WP (docs/v3/COVERAGE.md).
import type { ComponentType } from "react";
import { PAGES } from "@convenor/shared";
import { useDemoMode, useMeta } from "../../api/meta";
import { extra, page, screen, type AreaRoute, type PageLoader } from "../../routes/types";
import { DemoEntry } from "./components/DemoEntry";

/**
 * In the public demo no sign-in or sign-up form can work, so these pages show the
 * "Explore the demo" entry instead. Nothing is shown until /api/meta has loaded.
 */
export function demoGated(load: PageLoader): PageLoader {
  return () =>
    load().then((m) => {
      const Real: ComponentType = m.default;
      function Gated() {
        const demo = useDemoMode();
        const { appName } = useMeta();
        if (demo === undefined) return null;
        return demo ? <DemoEntry appName={appName} /> : <Real />;
      }
      return { default: Gated };
    });
}

export const routes: AreaRoute[] = [
  // 0. Access and accounts
  screen(1, demoGated(page(() => import("./pages/LoginPage"), "LoginPage"))),
  screen(2, demoGated(page(() => import("./pages/LoginTeamPage"), "LoginTeamPage"))),
  screen(3, demoGated(page(() => import("./pages/SignupPage"), "SignupPage"))),
  screen(4, demoGated(page(() => import("./pages/LoginExpertPage"), "LoginExpertPage"))),
  screen(5, demoGated(page(() => import("./pages/SignupExpertPage"), "SignupExpertPage"))),
  screen(6, demoGated(page(() => import("./pages/ForgotPasswordPage"), "ForgotPasswordPage"))),
  screen(7, demoGated(page(() => import("./pages/ResetPasswordPage"), "ResetPasswordPage"))),
  screen(8, demoGated(page(() => import("./pages/VerifyEmailPage"), "VerifyEmailPage"))),
  screen(9, demoGated(page(() => import("./pages/LoginVerifyPage"), "LoginVerifyPage"))),
  screen(10, demoGated(page(() => import("./pages/LoginMagicPage"), "LoginMagicPage"))),
  screen(11, demoGated(page(() => import("./pages/LoginSsoPage"), "LoginSsoPage"))),
  // 1. First-time organisation
  screen(12, demoGated(page(() => import("./pages/OrgStartPage"), "OrgStartPage"))),
  screen(13, demoGated(page(() => import("./pages/OrgWelcomePage"), "OrgWelcomePage"))),
  screen(14, page(() => import("./pages/WelcomePage"), "WelcomePage"), { allowNoOrg: true }),
  screen(15, page(() => import("./pages/OrgTeamPage"), "OrgTeamPage"), { allowNoOrg: true }),
  screen(16, page(() => import("./pages/OrgDefaultsPage"), "OrgDefaultsPage"), { allowNoOrg: true }),
  screen(17, page(() => import("./pages/OrgLibraryPage"), "OrgLibraryPage"), { allowNoOrg: true }),
  screen(18, page(() => import("./pages/OrgReadyPage"), "OrgReadyPage"), { allowNoOrg: true }),
  // Between the email code and the first project, while the free trial waits for payment details.
  extra(PAGES.welcomePayment, page(() => import("./pages/PaymentDetailsPage"), "PaymentDetailsPage")),
  // 10. Settings and access
  screen(138, page(() => import("./pages/TeamPage"), "TeamPage")),
  screen(139, page(() => import("./pages/InviteColleaguePage"), "InviteColleaguePage")),
  screen(140, page(() => import("./pages/ChangeRolePage"), "ChangeRolePage")),
  screen(141, page(() => import("./pages/PartnersPage"), "PartnersPage")),
  screen(142, page(() => import("./pages/PartnerAccessPage"), "PartnerAccessPage")),
  screen(143, page(() => import("./pages/AccountPage"), "AccountPage"), { allowNoOrg: true }),
  // GL3: every notification setting on one page (was three ticks on My account).
  extra(PAGES.settingsNotifications, page(() => import("./pages/NotificationSettingsPage"), "NotificationSettingsPage"), { allowNoOrg: true }),
  // EM-N1: the emails a test environment would have sent (only where no email is delivered).
  screen(150, page(() => import("./pages/OutboxPage"), "OutboxPage")),
  screen(149, page(() => import("./pages/BillingPage"), "BillingPage")),
  // Not sitemap screens
  extra(PAGES.signIn2faSetup, page(() => import("./pages/TwoStepSetupPage"), "TwoStepSetupPage")),
  extra(PAGES.signInLinkOpen, demoGated(page(() => import("./pages/SignInLinkPage"), "SignInLinkPage"))),
  extra(PAGES.signInDone, page(() => import("./pages/SignInDonePage"), "SignInDonePage")),
  extra(PAGES.joinTeam, demoGated(page(() => import("./pages/AcceptInvitePage"), "AcceptInvitePage"))),
  extra(PAGES.signOut, page(() => import("./pages/LogoutPage"), "LogoutPage")),
  // Old address: the organisation settings are merged into /settings/team.
  extra(PAGES.settingsOrganisation, page(() => import("./pages/OrgSettingsPage"), "OrgSettingsPage")),
];
